/**
 * <product-share>
 * Share buttons for a product. Every link is rebuilt from the current page URL
 * at click time, so a selected variant (?variant=) is shared too.
 */
const KEEP_PARAMS = ['variant'];
const INSTAGRAM_URL = 'https://www.instagram.com/direct/inbox/';

class ProductShare extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.status = this.querySelector('.product-share__status');
    this.statusTimer = null;

    const native = this.querySelector('.product-share__native');
    if (native && typeof navigator.share === 'function') native.hidden = false;

    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get shareUrl() {
    try {
      const base = new URL(this.dataset.url, window.location.origin);
      const current = new URL(window.location.href);
      if (current.pathname === base.pathname) {
        KEEP_PARAMS.forEach((param) => {
          const value = current.searchParams.get(param);
          if (value) base.searchParams.set(param, value);
        });
      }
      return base.toString();
    } catch (error) {
      return this.dataset.url;
    }
  }

  buildHref(network, url) {
    const text = this.dataset.text || this.dataset.title || '';
    const image = this.dataset.image || '';
    const enc = encodeURIComponent;

    switch (network) {
      case 'whatsapp':
        return `https://wa.me/?text=${enc(`${text} ${url}`)}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
      case 'pinterest':
        return `https://pinterest.com/pin/create/button/?url=${enc(url)}&media=${enc(image)}&description=${enc(text)}`;
      case 'x':
        return `https://x.com/intent/post?text=${enc(text)}&url=${enc(url)}`;
      case 'email':
        return `mailto:?subject=${enc(text)}&body=${enc(url)}`;
      default:
        return null;
    }
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const link = target.closest('a[data-network]');
    if (link) {
      const href = this.buildHref(link.dataset.network, this.shareUrl);
      if (href) link.href = href;
      return;
    }

    const button = target.closest('button[data-action]');
    if (!button) return;

    const action = button.dataset.action;
    if (action === 'copy') this.copyLink(this.dataset.copiedMessage);
    if (action === 'native') this.nativeShare();
    if (action === 'instagram') this.shareToInstagram();
  };

  async nativeShare() {
    try {
      await navigator.share({ title: this.dataset.title, text: this.dataset.text, url: this.shareUrl });
      return true;
    } catch (error) {
      // AbortError means the customer closed the sheet: nothing to do.
      return error && error.name === 'AbortError';
    }
  }

  /**
   * Instagram has no web link that pre-fills a post or message.
   * - Phones: open the device share sheet, where Instagram is listed.
   * - Computers: copy the link, then open Instagram messages in a new tab
   *   so the customer can paste it straight away.
   * The copy starts before window.open so the page still has focus, and
   * window.open runs synchronously inside the click so it is not blocked.
   */
  shareToInstagram() {
    const isTouch = window.matchMedia('(pointer: coarse)').matches;

    if (isTouch && typeof navigator.share === 'function') {
      this.nativeShare().then((shared) => {
        if (!shared) this.copyLink(this.dataset.instagramMessage);
      });
      return;
    }

    this.copyLink(this.dataset.instagramMessage);
    window.open(INSTAGRAM_URL, '_blank', 'noopener');
  }

  async copyLink(message) {
    const url = this.shareUrl;
    let copied = false;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        copied = true;
      }
    } catch (error) {
      copied = false;
    }

    if (!copied) {
      const field = document.createElement('textarea');
      field.value = url;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      field.remove();
    }

    this.announce(copied ? message : this.dataset.errorMessage);
  }

  announce(message) {
    if (!this.status || !message) return;
    clearTimeout(this.statusTimer);
    this.status.textContent = '';
    // Re-set on the next frame so screen readers announce repeated messages.
    requestAnimationFrame(() => {
      this.status.textContent = message;
    });
    this.statusTimer = setTimeout(() => {
      this.status.textContent = '';
    }, 6000);
  }
}

if (!customElements.get('product-share')) {
  customElements.define('product-share', ProductShare);
}
